import {
  PASSWORD_PROVIDER_ID,
  type ResetProvidersRequest,
  type ResetProvidersResponse,
  resetProvidersUrl,
} from '@words-happen/app-urls';
import type { SocialProvider } from '@words-happen/ui-components';

/**
 * The provider IDs of the account behind a password reset link (POST /auth/reset-providers on words-happen-gql).
 * Rejects with ResetProvidersError when they can't be had for any reason.
 */
export type ResetProviders = (oobCode: string) => Promise<string[]>;

/** Why the providers couldn't be had: the HTTP status (400, 429, 5xx, …), or `network`. Never says more. */
export class ResetProvidersError extends Error {
  constructor(readonly status: number | 'network' | 'malformed') {
    super(`Reset providers check failed (${status})`);
    this.name = 'ResetProvidersError';
  }
}

const isProviderList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((id) => typeof id === 'string');

/** POST /auth/reset-providers on `apiOrigin`. No cookies are sent: the oobCode is the only credential. */
export const createResetProviders =
  (apiOrigin: string, fetchImpl: typeof fetch = fetch): ResetProviders =>
  async (oobCode) => {
    let response: Response;
    try {
      response = await fetchImpl(resetProvidersUrl(apiOrigin), {
        method: 'POST',
        credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ oobCode } satisfies ResetProvidersRequest),
      });
    } catch {
      throw new ResetProvidersError('network');
    }
    if (!response.ok) {
      throw new ResetProvidersError(response.status);
    }
    let providers: unknown;
    try {
      ({ providers } = (await response.json()) as Partial<ResetProvidersResponse>);
    } catch {
      throw new ResetProvidersError('malformed');
    }
    if (!isProviderList(providers)) {
      throw new ResetProvidersError('malformed');
    }
    return providers;
  };

/** POST /auth/reset-providers on this build's API_ORIGIN, read on use so the module imports anywhere. */
export const browserResetProviders: ResetProviders = (oobCode) =>
  createResetProviders(API_ORIGIN, (...args) => fetch(...args))(oobCode);

/** Identity Platform provider IDs of the social providers this app names, in the order it shows them. */
const SOCIAL_PROVIDER_IDS: Readonly<Record<string, SocialProvider>> = {
  'apple.com': 'apple',
  'google.com': 'google',
  'microsoft.com': 'microsoft',
};

/**
 * Whether a reset link's account may set a password: one with a password may, with or without a social provider, as
 * may one with neither (an email-link-only account, decision 4 of ENG-849). One with a social provider and no password
 * may not, and `providers` names the ones this app knows (it may be empty for a provider it doesn't).
 */
export type ResetEligibility = { kind: 'password' } | { kind: 'social-only'; providers: SocialProvider[] };

export const resetEligibility = (providerIds: readonly string[]): ResetEligibility => {
  const social = providerIds.filter((id) => id !== PASSWORD_PROVIDER_ID);
  if (providerIds.includes(PASSWORD_PROVIDER_ID) || social.length === 0) {
    return { kind: 'password' };
  }
  const named = new Set(social.map((id) => SOCIAL_PROVIDER_IDS[id]).filter((provider) => provider !== undefined));
  return { kind: 'social-only', providers: Object.values(SOCIAL_PROVIDER_IDS).filter((p) => named.has(p)) };
};
